import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hasAnyRole } from '@court-sync/shared';
import { useAuthStore } from '@/store/auth';
import { userOrgService, type UserOrganizationEntry } from '@/services/userOrgs';
import { Button, Card, CardContent, CardHeader } from '@/components/ui';
import { Building2, IdCard } from 'lucide-react';

function RedirectToOrg({ slug }: { slug: string }) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(`/club-academy/${slug}/dashboard`, { replace: true });
  }, [navigate, slug]);
  return <p className="text-gray-600 dark:text-gray-400">Redirecting...</p>;
}

function roleSummary(org: UserOrganizationEntry): string {
  const parts: string[] = [];
  if (org.hasLessonsPortal && (org.roles.includes('guardian') || org.roles.includes('student'))) {
    parts.push('Lessons');
  }
  if (org.hasMembershipPortal && org.roles.includes('member')) {
    parts.push('Membership');
  }
  return parts.length > 0 ? parts.join(' · ') : 'Member portal';
}

export default function OrgPickerPage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState<UserOrganizationEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !hasAnyRole(user, ['guardian', 'student', 'member'])) {
      navigate('/dashboard');
      return;
    }
    void fetchOrgs();
  }, [user, navigate]);

  const fetchOrgs = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await userOrgService.getMyOrganizations();
      const data = res.data ?? [];
      setOrgs(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load organizations');
      setOrgs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOrg = (slug: string) => {
    navigate(`/club-academy/${slug}/dashboard`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dashboard-canvas">
        <p className="text-gray-600 dark:text-gray-400">Loading your organizations...</p>
      </div>
    );
  }

  if (orgs.length === 1) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dashboard-canvas">
        <RedirectToOrg slug={orgs[0].slug} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dashboard-canvas py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Select an organization</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-8">
          Choose where you want to go — lessons, membership, or both.
        </p>

        {error ? (
          <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
            {error}
          </div>
        ) : null}

        {orgs.length > 0 ? (
          <div className="space-y-4">
            {orgs.map((org) => (
              <Card
                key={org.organizationId}
                className="cursor-pointer hover:border-primary-500 transition-colors"
                onClick={() => handleSelectOrg(org.slug)}
              >
                <CardHeader className="flex flex-row items-center gap-3">
                  {org.hasMembershipPortal && !org.hasLessonsPortal ? (
                    <IdCard className="h-10 w-10 text-primary-600" />
                  ) : (
                    <Building2 className="h-10 w-10 text-primary-600" />
                  )}
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{org.name}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{roleSummary(org)}</p>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="pt-6">
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                You don&apos;t belong to any organizations yet. Visit an academy or club to get started.
              </p>
              <Button onClick={() => navigate('/login')}>Go to login</Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
